import { LoadState } from "../../constants/enums";
import { IWebrtcPublicConfig, IWebrtcConfig } from "../../services/webrtc/webrtcApi";
import { SipClientStatus, SipCallInfo } from "../../services/webrtc/sipClient";

export type CallProgressStatus =
  | "idle"
  | "calling"
  | "ringing"
  | "connecting"
  | "connected"
  | "on-hold"
  | "transferring"
  | "ended"
  | "failed";

export type TransferStatus = "idle" | "selecting" | "transferring" | "completed" | "failed"
  | "consulting" | "consult-ringing" | "consult-connected" | "consult-failed";

// --- Multi-call types ---

export type SessionStatus = "ringing" | "active" | "held" | "merging" | "ended";

export interface CallSessionInfo {
  id: string;
  direction: "inbound" | "outbound";
  remoteIdentity: string;
  displayName: string;
  startTime: number;
  holdStartTime: number | null;
  status: SessionStatus;
  isMuted: boolean;
}

export interface ConferenceState {
  active: boolean;
  participantIds: string[];
  startTime: number | null;
}

export interface IWebrtcState {
  // Public config (fetched at app startup)
  publicConfig: IWebrtcPublicConfig | null;
  publicConfigLoading: LoadState;

  // Full config (fetched when extension is known)
  fullConfig: Omit<IWebrtcConfig, "password"> | null;
  fullConfigLoading: LoadState;

  // SIP registration status
  sipStatus: SipClientStatus;

  // Current call info
  currentCall: SipCallInfo | null;

  // Call progress status (shown in UI)
  callProgress: CallProgressStatus;

  // ICE connection state (for diagnostics)
  iceState: string | null;

  // Incoming call pending acceptance
  incomingCall: SipCallInfo | null;

  // Errors
  error: string | null;
  registrationError: string | null;

  // Admin enable action
  enableLoading: LoadState;
  enableError: string | null;

  // Mic permission
  micPermission: "prompt" | "granted" | "denied";

  // Call transfer
  transferStatus: TransferStatus;
  transferError: string | null;

  // Attended transfer — target extension info
  attendedTransferTarget: string | null;
  consultCallInfo: SipCallInfo | null;

  // Dialer open request (from table phone icons etc.)
  dialerOpen: boolean;
  dialerNumber: string;

  // --- Multi-call fields ---
  sessions: Record<string, CallSessionInfo>;
  activeSessionId: string | null;
  conference: ConferenceState;
  swapInProgress: boolean;
  multiCallError: string | null;
  waitingCallId: string | null;
}

export const initialWebrtcState: IWebrtcState = {
  publicConfig: null,
  publicConfigLoading: LoadState.NotLoaded,
  fullConfig: null,
  fullConfigLoading: LoadState.NotLoaded,
  sipStatus: "idle",
  currentCall: null,
  callProgress: "idle",
  iceState: null,
  incomingCall: null,
  error: null,
  registrationError: null,
  enableLoading: LoadState.NotLoaded,
  enableError: null,
  micPermission: "prompt",
  transferStatus: "idle",
  transferError: null,
  attendedTransferTarget: null,
  consultCallInfo: null,
  dialerOpen: false,
  dialerNumber: "",

  // Multi-call defaults
  sessions: {},
  activeSessionId: null,
  conference: { active: false, participantIds: [], startTime: null },
  swapInProgress: false,
  multiCallError: null,
  waitingCallId: null,
};
